<template>
  <m-dialog
    :value="value"
    :title="skill === null ? $t('aiChat.skills.new') : $t('aiChat.skills.edit')"
    @update:value="$emit('update:value', $event)"
    @close="close"
  >
    <div class="ai-chat-skill-form">
      <div class="_label">
        {{ $t('aiChat.skills.titleLabel') }}
      </div>
      <m-text-field
        v-model:value="title"
        class="_title"
        :placeholder="$t('aiChat.skills.titlePlaceholder')"
        :max-length="TITLE_LIMIT"
        auto-focus
        has-background
      />
      <div class="_label">
        {{ $t('aiChat.skills.bodyLabel') }}
      </div>
      <div
        class="_body"
        @click="focusBody"
      >
        <m-editor
          ref="editor"
          :initial-value="initialBody"
          :allowed-content="AI_CHAT_PROMPT_CONTENT"
          :placeholder="$t('aiChat.skills.bodyPlaceholder')"
          disable-mobile-menu
          mobile-mentions
          @input="onInput"
        />
      </div>
    </div>
    <template #footer>
      <m-dialog-actions
        :ok-text="$t('general.save')"
        :disabled="saving"
        :submit-disabled="!canSave"
        @cancel="close"
        @ok="save"
      />
    </template>
  </m-dialog>
</template>

<script setup>
import MEditor from '@/components/editor/MEditor.vue';
import useAiChatSkills from '@/composables/ai-chat/ai-chat-skills';
import useSnackBar from '@/composables/snackbar';
import { AI_CHAT_PROMPT_CONTENT } from '@/lib/constants';
import { computed, ref } from 'vue';
import { docToMarkdown } from '@/lib/markdown';
import { logCatch } from '@/lib/logger/logger';

const TITLE_LIMIT = 100;

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  skill: {
    type: Object,
    default: null,
  },
  prompt: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:value']);

const snackbar = useSnackBar();
const { createSkill, updateSkill } = useAiChatSkills();

const initialBody = props.skill?.body ?? props.prompt;
const title = ref(props.skill?.title ?? '');
const body = ref(initialBody);

const saving = ref(false);
const canSave = computed(() => !saving.value && title.value.trim() !== '' && docToMarkdown(body.value) !== '');

const editor = ref(null);
const focusBody = () => editor.value.focus();

const onInput = (doc) => { body.value = doc; };

const close = () => emit('update:value', false);

const save = () => {
  if (!canSave.value) {
    return;
  }

  const skill = { title: title.value.trim(), body: body.value };
  const request = props.skill === null ? createSkill(skill) : updateSkill({ ...skill, uid: props.skill.uid });
  saving.value = true;
  request
    .then(close)
    .catch(logCatch(() => { snackbar.error(); }))
    .finally(() => { saving.value = false; });
};
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  .ai-chat-skill-form {
    ._label {
      margin-top: .8rem;
      margin-bottom: .4rem;
      color: $font-color-secondary;
    }

    ._body {
      min-height: 12rem;
      padding: .6rem 1.1rem;
      cursor: text;
      background-color: map_get($grey, 'lighten-7');
      border: 1px solid $input-border-color;
      border-radius: $input-field-border-radius;

      &:focus-within {
        border-color: $primary-color;
        box-shadow: 0 0 0 2px rgb(59 177 207 / 20%);
      }
    }
  }
</style>
